import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCategoryById, GENDERS } from '../../data/categories';
import { getPopularSizes } from '../../data/popularSizes';
import {
  convertSize,
  formatResultSummary,
  getAdjacentSizes,
  getSizeOptions,
  getSystemsForCategory,
  resolveGender,
} from '../../utils/convertSize';
import { buildShareParams, parseShareParams, buildShareUrl } from '../../utils/shareUrl';
import { loadRecentHistory, saveRecentHistory } from '../../utils/recentHistory';
import CategorySelector from './CategorySelector';
import SystemSelector from './SystemSelector';
import SizeInput from './SizeInput';
import ResultCards from './ResultCards';
import ConverterSteps from './ConverterSteps';
import QuickSizePicks from './QuickSizePicks';
import MeasurementFinder from './MeasurementFinder';
import ModeTabs from './ModeTabs';
import BrandFitNotes from './BrandFitNotes';
import RecentHistory from './RecentHistory';
import CompareSizes from './CompareSizes';

export default function SizeConverter({ t, tablePick, onTablePickHandled }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = useState('convert');
  const [categoryId, setCategoryId] = useState('shoes');
  const [gender, setGender] = useState('men');
  const [systemId, setSystemId] = useState('eu');
  const [sizeValue, setSizeValue] = useState('');
  const [fitPreference, setFitPreference] = useState('regular');
  const [selectedBrand, setSelectedBrand] = useState('general');
  const [recentItems, setRecentItems] = useState([]);

  const category = getCategoryById(categoryId);
  const resolvedGender = resolveGender(categoryId, gender);
  const showGender = category && category.genders.length > 1;

  const systems = useMemo(() => getSystemsForCategory(categoryId), [categoryId]);
  const sizeOptions = useMemo(
    () => getSizeOptions(categoryId, resolvedGender, systemId),
    [categoryId, resolvedGender, systemId]
  );
  const popular = useMemo(
    () => getPopularSizes(categoryId, resolvedGender, systemId),
    [categoryId, resolvedGender, systemId]
  );

  const result = useMemo(() => {
    if (!sizeValue || mode !== 'convert') return null;
    return convertSize({
      categoryId,
      gender: resolvedGender,
      systemId,
      sizeValue,
    });
  }, [sizeValue, categoryId, resolvedGender, systemId, mode]);

  const neighborSizes = useMemo(() => {
    if (!result?.row) return { smaller: null, larger: null };
    return getAdjacentSizes(categoryId, resolvedGender, result.row);
  }, [result, categoryId, resolvedGender]);

  const currentStep = useMemo(() => {
    if (result) return 3;
    if (sizeValue) return 2;
    if (systemId) return 1;
    return 0;
  }, [result, sizeValue, systemId]);

  const syncUrl = useCallback(
    (next) => {
      const qs = buildShareParams({
        categoryId: next.categoryId ?? categoryId,
        gender: next.gender ?? resolvedGender,
        systemId: next.systemId ?? systemId,
        sizeValue: next.sizeValue ?? sizeValue,
      });
      navigate({ pathname: location.pathname, search: `?${qs}` }, { replace: true });
    },
    [navigate, location.pathname, categoryId, resolvedGender, systemId, sizeValue]
  );

  const categoryInit = useRef(true);

  useEffect(() => {
    setRecentItems(loadRecentHistory());
  }, []);

  useEffect(() => {
    const parsed = parseShareParams(location.search);
    if (parsed.categoryId) setCategoryId(parsed.categoryId);
    if (parsed.gender) setGender(parsed.gender);
    if (parsed.systemId) setSystemId(parsed.systemId);
    if (parsed.sizeValue) {
      setSizeValue(parsed.sizeValue);
      setMode('convert');
    }
  }, [location.search]);

  useEffect(() => {
    const cat = getCategoryById(categoryId);
    if (cat?.defaultGender) setGender(cat.defaultGender);
    const sys = getSystemsForCategory(categoryId);
    if (sys.length && !sys.find((s) => s.id === systemId)) {
      setSystemId(sys[0].id);
    }
    if (categoryInit.current) {
      categoryInit.current = false;
      return;
    }
    setSizeValue('');
    setFitPreference('regular');
  }, [categoryId]);

  useEffect(() => {
    if (!tablePick) return;
    setCategoryId(tablePick.categoryId);
    if (tablePick.gender) setGender(tablePick.gender);
    setSystemId(tablePick.systemId);
    setSizeValue(tablePick.sizeValue);
    setMode('convert');
    onTablePickHandled?.();
    document.getElementById('size-converter')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [tablePick, onTablePickHandled]);

  useEffect(() => {
    if (sizeValue && mode === 'convert') {
      syncUrl({ categoryId, gender: resolvedGender, systemId, sizeValue });
    }
  }, [sizeValue, categoryId, resolvedGender, systemId, mode]);

  useEffect(() => {
    if (!result?.row) return;
    const entry = {
      categoryId,
      gender: resolvedGender,
      systemId,
      sizeValue,
    };
    saveRecentHistory(entry);
    setRecentItems(loadRecentHistory());
  }, [result?.row, categoryId, resolvedGender, systemId, sizeValue]);

  const handleMeasurementResult = (match) => {
    setMode('convert');
    if (categoryId === 'shoes' || categoryId === 'kids-shoes') {
      setSystemId('eu');
      setSizeValue(match.row.eu);
    } else if (categoryId === 'jeans') {
      setSystemId('waistIn');
      setSizeValue(match.row.waistIn);
    } else {
      setSystemId('international');
      setSizeValue(match.row.international);
    }
  };

  const applyRecent = (item) => {
    setCategoryId(item.categoryId);
    setGender(item.gender);
    setSystemId(item.systemId);
    setSizeValue(item.sizeValue);
    setMode('convert');
  };

  const shareUrl = buildShareUrl(location.pathname, {
    categoryId,
    gender: resolvedGender,
    systemId,
    sizeValue,
  });

  const summaryText = result
    ? `${t.hero.h1}\n${formatResultSummary(result, t.results, categoryId)}\n${shareUrl}`
    : '';

  const modes = [
    { id: 'convert', label: t.converter.modes.convert },
    { id: 'measure', label: t.converter.modes.measure },
    { id: 'compare', label: t.converter.modes.compare },
  ];

  return (
    <section
      id="size-converter"
      className="rounded-2xl border border-slate-700 bg-slate-800/40 p-6 sm:p-8 shadow-xl"
      aria-labelledby="converter-tool-heading"
    >
      <h2 id="converter-tool-heading" className="sr-only">
        {t.converter.toolHeading}
      </h2>
      <ConverterSteps steps={t.converter.steps} currentStep={currentStep} />

      <RecentHistory
        t={t}
        items={recentItems}
        onSelect={applyRecent}
        onClear={() => setRecentItems([])}
      />

      <ModeTabs modes={modes} active={mode} onChange={setMode} />

      <div className="space-y-6">
        <CategorySelector
          value={categoryId}
          onChange={setCategoryId}
          label={t.converter.category}
        />

        {showGender && (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t.converter.gender}
            </label>
            <div className="flex gap-2">
              {GENDERS.filter((g) => category.genders.includes(g.id)).map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGender(g.id)}
                  className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${
                    gender === g.id
                      ? 'bg-emerald-600 border-emerald-500 text-white'
                      : 'bg-slate-800 border-slate-700 text-slate-200'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <BrandFitNotes
          t={t}
          selectedBrand={selectedBrand}
          onBrandChange={setSelectedBrand}
        />

        {mode === 'compare' ? (
          <CompareSizes
            t={t}
            categoryId={categoryId}
            gender={resolvedGender}
            systemId={systemId}
            onSystemChange={setSystemId}
          />
        ) : mode === 'measure' ? (
          <MeasurementFinder
            t={t}
            categoryId={categoryId}
            gender={resolvedGender}
            onResult={handleMeasurementResult}
          />
        ) : (
          <>
            <SystemSelector
              label={t.converter.system}
              systems={systems}
              value={systemId}
              onChange={setSystemId}
              systemLabels={t.systems}
            />

            <QuickSizePicks
              label={t.converter.quickPicks}
              sizes={popular}
              activeValue={sizeValue}
              onPick={setSizeValue}
            />

            <SizeInput
              label={t.converter.size}
              placeholder={t.converter.sizePlaceholder}
              options={sizeOptions}
              value={sizeValue}
              onChange={setSizeValue}
            />
          </>
        )}

        {mode === 'convert' && sizeValue && !result && (
          <p className="text-amber-400/90 text-sm">{t.converter.noMatch}</p>
        )}

        {mode === 'convert' && (
          <ResultCards
            result={result}
            categoryId={categoryId}
            t={t}
            inputSystem={systemId}
            neighborSizes={neighborSizes}
            fitPreference={fitPreference}
            onFitChange={setFitPreference}
            onPickSize={setSizeValue}
            summaryText={summaryText}
            shareUrl={shareUrl}
          />
        )}
      </div>
    </section>
  );
}
